import React, { useEffect, useState } from "react";
import {
  ALLOWLIST_ENABLED,
  WAREHOUSE_ALLOWLIST,
  isIpAllowed,
  fetchPublicIp,
  hasActiveBypass,
  consumeBypassFromUrl,
} from "@/lib/warehouseAllowlist";
import { ShieldSlash, Warning } from "@phosphor-icons/react";

/**
 * WarehouseGate — client-side splash that blocks the UI when the visitor's
 * public IP isn't in the warehouse allowlist.
 *
 * States:
 *   - "checking":  Fetching public IP. Shows a subtle loading indicator.
 *   - "allowed":   Renders `children` (i.e. the rest of the app).
 *   - "denied":    Renders the "wrong network" splash.
 *
 * A soft gate — real security must live in the backend allowlist middleware.
 * See `docs/BACKEND_IP_ALLOWLIST_SPEC.md` for the backend team spec.
 */
export default function WarehouseGate({ children }) {
  const [status, setStatus] = useState("checking");
  const [visitorIp, setVisitorIp] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ALLOWLIST_ENABLED || WAREHOUSE_ALLOWLIST.length === 0) {
      // Gate disabled or unconfigured — always allow.
      setStatus("allowed");
      return;
    }
    // Consume ?bypass=... from URL BEFORE the IP check.
    consumeBypassFromUrl();
    if (hasActiveBypass()) {
      setStatus("allowed");
      return;
    }
    const controller = new AbortController();
    fetchPublicIp({ signal: controller.signal })
      .then((ip) => {
        setVisitorIp(ip);
        setStatus(isIpAllowed(ip) ? "allowed" : "denied");
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        // Fail-open: if we can't determine the IP, let the user in rather
        // than lock the warehouse out because ipify is down.
        console.warn("IP lookup failed — allowing access:", err);
        setError(err?.message || String(err));
        setStatus("allowed");
      });
    return () => controller.abort();
  }, []);

  if (status === "checking") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-zinc-950 text-zinc-400 text-sm font-semibold uppercase tracking-widest">
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse mr-2" />
        Verifying network…
      </div>
    );
  }

  if (status === "denied") {
    return <AccessDeniedSplash visitorIp={visitorIp} />;
  }

  return children;
}

function AccessDeniedSplash({ visitorIp }) {
  return (
    <div
      data-testid="warehouse-access-denied"
      className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-950 to-red-950/40 p-6"
    >
      <div className="max-w-lg w-full bg-zinc-900 border border-red-900/50 rounded-lg overflow-hidden shadow-2xl">
        <div className="bg-red-600 text-white px-6 py-4 flex items-center gap-3">
          <ShieldSlash size={28} weight="fill" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-80">Cabinet Depot</div>
            <div className="font-heading font-black text-xl leading-tight">Access restricted</div>
          </div>
        </div>
        <div className="px-6 py-6 space-y-4 text-zinc-200">
          <p className="text-sm">
            This desktop app is only accessible from within the Cabinet Depot
            warehouse network. Your current connection isn't recognised.
          </p>
          <div className="rounded-md bg-zinc-950 border border-zinc-800 px-4 py-3 text-xs font-mono">
            <div className="text-zinc-500 uppercase tracking-widest text-[10px] mb-1 font-sans font-bold">Your IP</div>
            <div className="text-zinc-100">{visitorIp || "unknown"}</div>
          </div>
          <div className="rounded-md bg-amber-950/30 border border-amber-900/50 px-4 py-3 text-xs text-amber-200 flex gap-2">
            <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
            <div>
              If you're a manager troubleshooting off-site, append
              <span className="font-mono px-1 py-0.5 rounded bg-amber-900/40 mx-1">?bypass=&lt;code&gt;</span>
              to the URL. Ask your admin for the current code.
            </div>
          </div>
          <p className="text-xs text-zinc-500">
            All warehouse activity — scans, prints, sync — must originate from
            the shop floor to keep the audit trail clean.
          </p>
        </div>
      </div>
    </div>
  );
}
